/**
 * Matrice rôles × permissions — backoffice admin.
 *
 * - Lecture autorisée à admin et super_admin (route exposée pour les deux).
 * - Édition réservée à super_admin (le bouton Enregistrer est désactivé sinon,
 *   et le backend renvoie 403 PERMISSION_DENIED si l'admin tente le PUT).
 *
 * Refonte R9 (mai 2026) :
 *   - Header avec LogoMark Shield accent tech
 *   - Alert info si rôle non-super_admin (lecture seule)
 *   - Card glass avec table dense (4 + 5 colonnes)
 *   - Rows alternées hover surface/60
 *   - En-têtes de section par service en Badge tech
 *   - Footer avec 5 boutons (un par rôle) — primary si dirty, désactivé sinon
 *
 * Logique métier 100% préservée :
 *   - api.admin.roles.matrix + updatePermissions(role, codes)
 *   - canEdit = super_admin uniquement
 *   - draft local en `Record<UserRole, Set<string>>`
 *   - toggle(role, code) : ajoute/retire du Set
 *   - isDirty(role) : compare draft[role] vs matrix[role].permissionCodes (taille + contenu)
 *   - saveRole(role) : PUT remplace le set complet pour ce rôle, audit log diff granted/revoked
 *   - Grouped by service (Map sorted localeCompare)
 *   - Fragment au lieu de <> dans table (key par service)
 */

import { useState, useEffect, useCallback, useMemo, Fragment } from 'react';
import {
  Card, Button, Alert, Badge, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Save, ShieldAlert, Shield, AlertCircle, CheckCircle2, Lock } from 'lucide-react';
import type {
  AdminPermission,
  AdminRoleMatrixEntry,
  UserRole,
} from '@roronoa-collectivites/shared-types';

const ALL_ROLES: UserRole[] = ['super_admin', 'admin', 'manager', 'agent', 'administre'];

const ROLE_LABEL: Record<UserRole, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  manager: 'Manager',
  agent: 'Agent',
  administre: 'Administré',
};

export function PermissionsMatrixPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'super_admin';

  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [matrix, setMatrix] = useState<AdminRoleMatrixEntry[]>([]);
  // Socle verrouillé (migration 199) : { role → { code → motif } }.
  // Ces cases ne se décochent pas — leur retrait ne restreindrait pas un droit,
  // il couperait le service. L'affichage ci-dessous n'est qu'un miroir : la
  // garde réelle est côté API (403 PERMISSION_VERROUILLEE).
  const [verrous, setVerrous] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState<UserRole | null>(null);

  // Édition locale : { role → Set<permissionCode> }
  const [draft, setDraft] = useState<Record<UserRole, Set<string>>>(
    () =>
      Object.fromEntries(ALL_ROLES.map((r) => [r, new Set<string>()])) as Record<
        UserRole,
        Set<string>
      >
  );

  const fetchMatrix = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.admin.roles.matrix();
      setPermissions(res.permissions);
      setMatrix(res.matrix);
      const next: Record<UserRole, Set<string>> = Object.fromEntries(
        ALL_ROLES.map((r) => [r, new Set<string>()])
      ) as Record<UserRole, Set<string>>;
      const nextVerrous: Record<string, Record<string, string>> = {};
      for (const m of res.matrix) {
        next[m.role] = new Set(m.permissionCodes);
        nextVerrous[m.role] = Object.fromEntries(
          (m.verrous ?? []).map((v) => [v.code, v.motif])
        );
      }
      setDraft(next);
      setVerrous(nextVerrous);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.payload.error || 'Erreur de chargement');
      } else {
        setError('Erreur réseau');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  // Grouper par service
  const grouped = useMemo(() => {
    const map = new Map<string, AdminPermission[]>();
    for (const p of permissions) {
      if (!map.has(p.service)) map.set(p.service, []);
      map.get(p.service)!.push(p);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [permissions]);

  /** Cette case est-elle verrouillée (socle citoyen, migration 199) ? */
  function estVerrouillee(role: UserRole, code: string): boolean {
    return verrous[role]?.[code] !== undefined;
  }

  function toggle(role: UserRole, code: string) {
    if (!canEdit) return;
    // Un couple verrouillé ne se décoche pas : son retrait ne restreindrait pas
    // un droit, il couperait le service. L'API refuse de toute façon (403
    // PERMISSION_VERROUILLEE) — on évite juste d'afficher un état impossible.
    if (estVerrouillee(role, code)) return;
    setDraft((prev) => {
      const set = new Set(prev[role]);
      if (set.has(code)) set.delete(code);
      else set.add(code);
      return { ...prev, [role]: set };
    });
  }

  function isDirty(role: UserRole): boolean {
    const original = new Set(matrix.find((m) => m.role === role)?.permissionCodes ?? []);
    const current = draft[role];
    if (original.size !== current.size) return true;
    for (const c of current) if (!original.has(c)) return true;
    return false;
  }

  async function saveRole(role: UserRole) {
    if (!canEdit) return;
    setSavingRole(role);
    setError(null);
    setSuccess(null);
    try {
      const codes = Array.from(draft[role]);
      const res = await api.admin.roles.updatePermissions(role, { permissionCodes: codes });
      setMatrix(res.matrix);
      const next: Record<UserRole, Set<string>> = Object.fromEntries(
        ALL_ROLES.map((r) => [r, new Set<string>()])
      ) as Record<UserRole, Set<string>>;
      for (const m of res.matrix) {
        next[m.role] = new Set(m.permissionCodes);
      }
      setDraft(next);
      setSuccess(`Permissions du rôle « ${ROLE_LABEL[role]} » mises à jour.`);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.payload.error || 'Mise à jour refusée');
      } else {
        setError('Erreur réseau');
      }
    } finally {
      setSavingRole(null);
    }
  }

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-tech border-t-transparent"
          role="status"
          aria-label="Chargement"
        />
      </div>
    );
  }

  return (
    <section aria-labelledby="permissions-title" className="animate-fade-in">
      {/* En-tête */}
      <header className="mb-4 flex items-center gap-3">
        <LogoMark icon={Shield} accent="tech" size="lg" />
        <div>
          <SectionEyebrow as="div">Administration</SectionEyebrow>
          <h1
            id="permissions-title"
            className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl"
          >
            Permissions
          </h1>
          <p className="mt-0.5 text-sm text-ink-muted tabular-nums">
            Matrice {permissions.length} permissions × {ALL_ROLES.length} rôles
          </p>
        </div>
      </header>

      {!canEdit && (
        <Alert variant="info" icon={ShieldAlert} className="mb-4">
          La modification de la matrice est réservée au rôle <strong>super_admin</strong>. Vous
          consultez la matrice en lecture seule.
        </Alert>
      )}

      {error && (
        <Alert variant="error" icon={AlertCircle} className="mb-4">
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" icon={CheckCircle2} className="mb-4">
          {success}
        </Alert>
      )}

      <Card variant="glass" padded={false}>
        {/* R13.6 — Wrapper scroll horizontal avec min-width forcée pour mobile.
            Padding compact en mobile (px-2) puis confortable en sm+ (px-4).
            Hint visuel sur mobile : ombre droite indique qu'on peut scroller. */}
        <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
          <table className="w-full min-w-[640px]">
            <thead className="border-b border-border bg-surface/40 backdrop-blur-glass">
              <tr className="text-left">
                <th className="px-2 sm:px-4 py-3 text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium sticky left-0 bg-surface/95 backdrop-blur-glass z-10">
                  Permission
                </th>
                {ALL_ROLES.map((r) => (
                  <th key={r} className="px-2 sm:px-4 py-3 text-center text-2xs uppercase tracking-r-eyebrow text-ink-subtle font-medium whitespace-nowrap">
                    {ROLE_LABEL[r]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-sm">
              {grouped.map(([service, perms]) => (
                <Fragment key={`section-${service}`}>
                  <tr className="bg-surface/40 backdrop-blur-glass border-b border-border">
                    <td colSpan={ALL_ROLES.length + 1} className="px-2 sm:px-4 py-2">
                      <Badge variant="tech" size="sm">
                        {service}
                      </Badge>
                    </td>
                  </tr>
                  {perms.map((p) => (
                    <tr key={p.id} className="border-b border-border last:border-b-0 transition-colors hover:bg-surface/60">
                      <td className="px-2 sm:px-4 py-2.5 sticky left-0 bg-surface/95 backdrop-blur-glass">
                        <div className="font-medium text-ink text-sm">{p.libelle}</div>
                        <div className="font-mono text-2xs text-ink-muted">{p.code}</div>
                      </td>
                      {ALL_ROLES.map((r) => {
                        const checked = draft[r].has(p.code);
                        const motif = verrous[r]?.[p.code];
                        const verrouille = motif !== undefined;
                        return (
                          <td key={r} className="px-2 sm:px-4 py-2.5 text-center">
                            <label
                              className={
                                verrouille
                                  ? 'inline-flex cursor-not-allowed items-center justify-center gap-1'
                                  : 'inline-flex cursor-pointer items-center justify-center'
                              }
                              title={verrouille ? `Requis par le service : ${motif}` : undefined}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(r, p.code)}
                                disabled={!canEdit || verrouille}
                                aria-label={
                                  verrouille
                                    ? `${p.libelle} pour ${ROLE_LABEL[r]} — verrouillé : ${motif}`
                                    : `${p.libelle} pour ${ROLE_LABEL[r]}`
                                }
                                className="h-5 w-5 rounded border-border bg-surface/60 text-tech focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg disabled:opacity-50 disabled:cursor-not-allowed accent-tech"
                              />
                              {verrouille && (
                                <Lock
                                  className="h-3 w-3 shrink-0 text-ink-muted"
                                  aria-hidden="true"
                                />
                              )}
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {canEdit && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-surface/40 backdrop-blur-glass px-4 py-3">
            <span className="mr-auto text-2xs text-ink-muted">
              Chaque rôle est enregistré séparément. Les modifications sont audit-loggées avec diff
              granted/revoked.
            </span>
            {ALL_ROLES.map((r) => {
              const dirty = isDirty(r);
              return (
                <Button
                  key={r}
                  variant={dirty ? 'primary' : 'secondary'}
                  size="sm"
                  disabled={!dirty || savingRole === r}
                  onClick={() => saveRole(r)}
                >
                  <Save className="h-4 w-4" aria-hidden="true" />
                  {savingRole === r ? 'Enregistrement…' : `Enregistrer ${ROLE_LABEL[r]}`}
                </Button>
              );
            })}
          </div>
        )}
      </Card>
    </section>
  );
}
